/* eslint-disable react-hooks/exhaustive-deps */
import { StatusCode } from '@/api/enumerations';
import {
  getDelegatedSessions,
  getOversightUsersById,
  getSessionsByControlUser,
} from '@/api/oversight';
import {
  ControlSessions,
  ControlSessions as ControlSessionsType,
  DelegatedSessions,
  OversightUsers,
} from '@/api/oversight/types';
import { Search } from '@/components/Search';
import Snackbar from '@/components/Snackbar';
import {
  IconAdminPanelSettingsMS,
  IconArrowCircleLeftMS,
  IconArrowForwardIosMS,
  IconPageviewMS,
} from '@/constants/icons';
import { GlobalContext } from '@/context/global';
import { useDebounce } from '@/hooks/useDebounce';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import {
  delegatedAccessLabels,
  expiredDetail,
  formatDate,
  getTerminatedByLabel,
  normalizeStr,
} from '@/pages/Oversight/utils';
import { Search as SearchIcon } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useDefaultLayout } from 'react-resizable-panels';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '../../../components/Button';
import { Card, CardData } from '../../../components/Card';
import { ReasonAccordion } from '../../../components/ReasonAccordion';
import { Table } from '../../../components/Table';
import {
  StyledTableCellBody,
  SubtitleCell,
} from '../../../components/Table/styles';
import { TabsPanel } from '../../../components/TabsPanel';
import { TabItem } from '../../../components/TabsPanel/types';
import {
  BackButton,
  CardContainer,
  GridMainContainer,
  RoundedGreen,
  Section,
  StyledTitle,
} from '../../../styles';
import {
  delegatedAccessRow,
  sectionAdmUserRow,
} from '../../Clients/CustomerDetails/data';
import {
  ContainerGroup,
  Panel,
  StyledSeparator,
} from '../../Clients/CustomerDetails/styles';
import { AmountOFClients, TableDataName } from '../../Clients/styles';

export function Details(): JSX.Element {
  const [loading, setLoading] = useState(true);
  const [isShowDrawer, setIsShowDrawer] = useState(false);
  const [searchSessions, setSearchSessions] = useState('');
  const [tabs, setTabs] = useState<TabItem[]>([]);
  const [selectedTab, setSelectedTab] = useState<string>('');
  const [userData, setUserData] = useState<OversightUsers>();
  const [cardData, setCardData] = useState<CardData[]>([
    { label: 'Nome', value: '' },
    { label: 'Status do usuário', value: false, user_status: 'active' },
    { label: 'ID', value: 0 },
    { label: 'Tipo de usuário', value: '' },
    { label: 'Última atividade', value: '' },
    { label: 'E-mail', value: '' },
  ]);
  const [delegatedSessions, setDelegatedSessions] = useState<
    DelegatedSessions[]
  >([]);
  const [delegatedRecords, setDelegatedRecords] = useState(0);
  const [searchDelegatedSessions, setSearchDelegatedSessions] = useState('');
  const [delegatedPage, setDelegatedPage] = useState(1);
  const [sessionsRecords, setSessionsRecords] = useState(0);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [controlSessions, setControlSessions] = useState<ControlSessionsType[]>(
    []
  );

  const debounceDelegatedSessions = useDebounce(searchDelegatedSessions);
  const debounceSessions = useDebounce(searchSessions);

  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: 'oversight-adm-user-details',
    panelIds: isShowDrawer ? ['left', 'right'] : ['left'],
  });

  const { id } = useParams();
  const { flag } = useParams();
  const navigate = useNavigate();
  const { handleSnackbar } = useSnackbar();
  const { openSnackbar } = useContext(GlobalContext);
  const { getErrorMessage } = useErrorMessage();
  const { oversightUserRole } = useTokenRefresh();

  const titlePage = flag?.includes('administrative')
    ? 'Usuários Administrativos'
    : 'Clientes';

  const handleOpenActivityDelegatedAccess = (row: DelegatedSessions): void => {
    setIsShowDrawer(true);

    const existingTab = tabs.find((tab) => tab.source === 'delegated_access');

    const newTab: TabItem = {
      id: row.id,
      // Reaproveita a aba de acesso delegado aberta em vez de remontar o painel.
      value: existingTab?.value ?? crypto.randomUUID(),
      title: 'Acessos delegados',
      subtitle: row.client_name,
      source: 'delegated_access',
      delegatedSessionId: row.id,
      startedAt: row.session_started_at ?? undefined,
      last_activity_at: row.last_activity_at,
      userRole: oversightUserRole,
    };

    // Aberto pela tabela, o acesso delegado é um destino autônomo: ocupa o
    // painel sozinho.
    setTabs([newTab]);
    setSelectedTab(newTab.value);
  };

  const handleOpenActivitySectionUser = (
    row: typeof controlSessions[0]
  ): void => {
    setIsShowDrawer(true);

    const existingTab = tabs.find((tab) => tab.source === 'section_adm_user');
    if (existingTab) {
      if (existingTab.id === row.id) {
        setSelectedTab(existingTab.value);
        return;
      }

      const newTab: TabItem = {
        id: row.id,
        value: crypto.randomUUID(),
        title: 'Sessões do usuário administrador',
        subtitle: userData?.name || '',
        source: 'section_adm_user',
        sessionId: row.id,
        startedAt: row.created_at,
        last_activity_at: row.ended_at,
        userRole: oversightUserRole,
      };
      setTabs([newTab]);
      setSelectedTab(newTab.value);
      return;
    }

    const newTab: TabItem = {
      id: row.id,
      value: crypto.randomUUID(),
      title: 'Sessões do usuário administrador',
      subtitle: userData?.name || '',
      sessionId: row.id,
      startedAt: row.created_at || null,
      last_activity_at: row.ended_at,
      userRole: oversightUserRole,
      source: 'section_adm_user',
    };
    setTabs([newTab]);
    setSelectedTab(newTab.value);
  };

  const handleActivityClick = (delegatedSessionId: number): void => {
    const sessionTab = tabs.find((tab) => tab.source === 'section_adm_user');
    if (!sessionTab) return;

    const row = delegatedSessions.find(
      (session) => session.id === delegatedSessionId
    );
    const existingTab = tabs.find((tab) => tab.source === 'delegated_access');

    const newTab: TabItem = {
      id: delegatedSessionId,
      value: existingTab?.value ?? crypto.randomUUID(),
      title: 'Acessos delegados',
      subtitle: row?.client_name ?? '',
      source: 'delegated_access',
      delegatedSessionId,
      startedAt: row?.session_started_at ?? undefined,
      last_activity_at: row?.last_activity_at,
      userRole: oversightUserRole,
    };

    setTabs([sessionTab, newTab]);
    setSelectedTab(newTab.value);
  };

  const handleClose = (value: string): void => {
    const updated = tabs.filter((tab) => tab.value !== value);
    setTabs(updated);
    if (updated.length === 0) {
      setIsShowDrawer(false);
    }
  };

  const handleCloseAll = (): void => {
    setTabs([]);
    setIsShowDrawer(false);
  };

  const getUserById = useCallback(async () => {
    try {
      const { data, detail } = await getOversightUsersById(Number(id));

      if (detail?.description) throw new Error(detail?.description);

      if (detail?.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente');
      }

      setUserData(data);
      const status = data.is_active ? 'active' : 'inactive';

      setCardData([
        { label: 'Nome', value: data.name },
        {
          label: 'Status do usuário',
          value: data.is_active,
          user_status: status,
        },
        { label: 'ID', value: data.id },
        { label: 'Tipo de usuário', value: data.role },
        {
          label: 'Última atividade',
          value: data.last_session_ended_at
            ? formatDate(data.last_session_ended_at, 'relative')
            : '-',
        },
        { label: 'E-mail', value: data.email },
      ]);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  }, []);

  const getControlSessions = useCallback(
    async (search: string) => {
      try {
        const { data, detail } = await getSessionsByControlUser(
          Number(id),
          search,
          sessionsPage
        );

        if (detail?.description) throw new Error(detail.description);

        if (detail?.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente');
        }

        setControlSessions(data.control_sessions);
        setSessionsRecords(data.records);
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      }
    },
    [getErrorMessage, handleSnackbar, sessionsPage]
  );

  const getDelegatedAccess = useCallback(
    async (search: string) => {
      try {
        const { data, detail } = await getDelegatedSessions(
          Number(id),
          search,
          delegatedPage
        );

        if (detail?.description) throw new Error(detail.description);

        if (detail?.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente');
        }

        setDelegatedSessions(data.delegated_sessions);
        setDelegatedRecords(data.records);
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      }
    },
    [getErrorMessage, handleSnackbar, delegatedPage]
  );

  const renderDelegatedStatus = (row: DelegatedSessions): ReactNode => {
    const label = delegatedAccessLabels[row.status] ?? row.status;
    const detail = expiredDetail(row);

    if (!detail) {
      return label;
    }

    return (
      <>
        {label}
        <Tooltip title={detail.tooltip} placement="top">
          <SubtitleCell>{detail.label}</SubtitleCell>
        </Tooltip>
      </>
    );
  };

  const renderSessionStatus = (row: ControlSessions): ReactNode => {
    if (!row.ended_at) {
      return (
        <div>
          <RoundedGreen />
          Online
        </div>
      );
    }

    return (
      <>
        {row.terminated_by && getTerminatedByLabel(row.terminated_by)}
        <SubtitleCell>
          <span>{row.ended_at && formatDate(row.ended_at, 'absolute')}</span>
        </SubtitleCell>
      </>
    );
  };

  useEffect(() => {
    getUserById();

    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setDelegatedPage(1);
  }, [debounceDelegatedSessions]);

  useEffect(() => {
    getDelegatedAccess(normalizeStr(debounceDelegatedSessions));
  }, [debounceDelegatedSessions, delegatedPage]);

  useEffect(() => {
    setSessionsPage(1);
  }, [debounceSessions]);

  useEffect(() => {
    getControlSessions(normalizeStr(debounceSessions));
  }, [debounceSessions, sessionsPage]);

  return (
    <ContainerGroup
      orientation="horizontal"
      defaultLayout={defaultLayout}
      onLayoutChanged={onLayoutChanged}
    >
      <Panel minSize="65%">
        <GridMainContainer>
          <BackButton onClick={() => navigate(-1)}>
            {IconArrowCircleLeftMS}
          </BackButton>

          <StyledTitle>
            <>
              {IconAdminPanelSettingsMS} PROTO OVERSIGHT
              <span>
                {IconArrowForwardIosMS} {titlePage} {IconArrowForwardIosMS}{' '}
                {userData?.name}
              </span>
            </>
          </StyledTitle>

          <CardContainer>
            <Card
              title="Informações do usuário"
              cardData={cardData}
              isLoading={loading}
              qtdItems={6}
            />
          </CardContainer>

          <Section>
            <div className="values">
              <TableDataName>Acessos delegados</TableDataName>

              <AmountOFClients>{delegatedRecords} registros</AmountOFClients>
            </div>

            <Search
              value={searchDelegatedSessions}
              setSearchField={setSearchDelegatedSessions}
              placeholder="Busca por cliente, motivo ou status..."
              width="423px"
              withoutBorder
              maxLength={50}
            />
          </Section>

          <Table
            data={delegatedSessions}
            tableHeadData={delegatedAccessRow}
            getRowId={(row) => row.id}
            emptyTableDescription="Nenhum resultado encontrado"
            emptyTableIcon={<SearchIcon />}
            records={delegatedRecords}
            page={delegatedPage}
            setPage={setDelegatedPage}
            fixedLayout
            renderRow={(row) => (
              <>
                <StyledTableCellBody>{row.client_name}</StyledTableCellBody>
                <StyledTableCellBody>
                  {renderDelegatedStatus(row)}
                </StyledTableCellBody>
                <StyledTableCellBody>
                  <ReasonAccordion reason={row.reason} />
                </StyledTableCellBody>
                <StyledTableCellBody>
                  <Button
                    variant="outlined"
                    style={{ width: '100%' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenActivityDelegatedAccess(row);
                    }}
                    disabled={row.activity_count === 0}
                  >
                    Ver atividade (
                    {row.activity_count > 999 ? '999+' : row.activity_count})
                    {IconPageviewMS}
                  </Button>
                </StyledTableCellBody>
              </>
            )}
          />

          <Section>
            <div className="values">
              <TableDataName>Sessões do usuário administrativo</TableDataName>

              <AmountOFClients>{sessionsRecords} sessões</AmountOFClients>
            </div>

            <Search
              value={searchSessions}
              setSearchField={setSearchSessions}
              placeholder="Busca por status ou erro..."
              width="423px"
              withoutBorder
              maxLength={50}
            />
          </Section>

          <Table
            data={controlSessions}
            tableHeadData={sectionAdmUserRow}
            getRowId={(row) => row.id}
            onRowClick={(row) => row.id}
            emptyTableDescription="Nenhum resultado encontrado"
            emptyTableIcon={<SearchIcon />}
            records={sessionsRecords}
            page={sessionsPage}
            setPage={setSessionsPage}
            renderRow={(row) => (
              <>
                <StyledTableCellBody>
                  {row.created_at && formatDate(row.created_at, 'absolute')}
                </StyledTableCellBody>
                <StyledTableCellBody>
                  {renderSessionStatus(row)}
                </StyledTableCellBody>
                <StyledTableCellBody>
                  {row.delegated_session_count ?? '-'}
                </StyledTableCellBody>
                <StyledTableCellBody>
                  <Button
                    variant="outlined"
                    style={{ width: '100%' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenActivitySectionUser(row);
                    }}
                  >
                    Ver atividade (
                    {row.activity_count > 999 ? '999+' : row.activity_count})
                    {IconPageviewMS}
                  </Button>
                </StyledTableCellBody>
              </>
            )}
          />
        </GridMainContainer>
      </Panel>
      <StyledSeparator className="resize-handle" />
      {isShowDrawer && (
        <Panel>
          <TabsPanel
            tabs={tabs}
            selectedTab={selectedTab}
            onSelectedTabChange={setSelectedTab}
            onClose={handleClose}
            onCloseAll={handleCloseAll}
            onActivityClick={handleActivityClick}
          />
        </Panel>
      )}
      {openSnackbar && <Snackbar />}
    </ContainerGroup>
  );
}
